<template>
    <transition :name="$route.meta.transition || 'fade'">
        <div>
            <TickerTape v-if="tradingView" :options="tickerTapeOptions" />

            <div class="container mx-auto">
                <TradingroomHeader />

                <div id="content" class="flex flex-col lg:flex-row lg:divide-x divide-gray-300 py-16">
                    <div class="lg:w-3/4 lg:pr-8">
                        <div id="highlights" class="relative">
                            <strong class="mb-4 block">
                                {{ $t('news_financial_education') }}
                            </strong>

                            <div v-click-outside="() => (filterOpened = false)" class="lg:absolute top-0 right-0 z-50">
                                <div class="flex flex-row gap-2 items-center cursor-pointer pb-2" @click="filterOpened = !filterOpened">
                                    <Icon type="Filter" class="fill-current" viewBox="0 0 21 20" width="21" height="20" />
                                    <p>{{ $t('plain_filter') }}</p>
                                </div>
                                <div class="relative">
                                    <TransitionExpand v-show="filterOpened" class="absolute top-0 right-0 z-50 w-full lg:w-[600px]">
                                        <div class="mt-2 rounded-md shadow-md p-4 bg-white flex flex-col gap-4">
                                            <Select
                                                v-model:selected="sortBy"
                                                name="sort_by"
                                                :no-value-allowed="true"
                                                :form-component-id="$.uid"
                                                :label="$t('sort_by')"
                                                :options="sortByOptions"
                                                :multiple="false"
                                                class="w-full lg:w-1/3 self-end z-30 lg:pl-2"
                                            >
                                            </Select>
                                            <div class="flex flex-col lg:flex-row gap-4 lg:h-[320px]">
                                                <div v-if="filters.users?.data" class="w-full max-h-[240px] lg:max-h-full overflow-y-auto">
                                                    <p>{{ $t('filter_by_authors') }}</p>
                                                    <div v-for="user in filters.users.data" :key="user.id">
                                                        <Checkbox
                                                            :name="$translate(user.first_name) + ' ' + $translate(user.last_name)"
                                                            :label="$translate(user.first_name) + ' ' + $translate(user.last_name)"
                                                            :value="selectedUsers.includes(user.id)"
                                                            class="my-4"
                                                            @update:value="selectUser(user.id)"
                                                        />
                                                    </div>
                                                </div>
                                                <Datepicker
                                                    :format="calendarModeltype"
                                                    class="!m-0"
                                                    :class="filters.users?.data ? 'w-full' : 'w-1/2 lg:w-full'"
                                                    :model-value="date"
                                                    :locale="calendarLocale"
                                                    :select-text="$t('plain_select')"
                                                    :enable-time-picker="false"
                                                    :range="true"
                                                    inline
                                                    auto-apply
                                                    :monday-first="true"
                                                    @update:model-value="setDate"
                                                />
                                            </div>

                                            <hr class="border-gray-500 w-full my-4" />

                                            <div class="flex flex-row gap-2 justify-between">
                                                <p class="text-blue cursor-pointer" @click="reset()">{{ $t('plain_reset') }}</p>
                                                <p class="text-blue cursor-pointer" @click="search()">{{ $t('plain_apply') }}</p>
                                            </div>
                                        </div>
                                    </TransitionExpand>
                                </div>
                            </div>

                            <Loading :loading="loading">
                                <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                    <TradingRoomPostItem
                                        v-for="item in $store.getters['posts/getByKey']('index').data"
                                        :key="item.id"
                                        layout="no_image"
                                        class="bg-white shadow-md rounded-md"
                                        :contentstream="item"
                                    />
                                </div>
                            </Loading>

                            <div class="flex flex-row justify-between mt-4">
                                <router-link :to="link" class="flex flex-row gap-2 group-hover:gap-3 transition-all ease-in-out duration-500 items-center mt-2 cursor-pointer">
                                    <span>
                                        {{ $t('tradingroom') }}
                                    </span>
                                    <Icon type="ArrowRight" viewBox="0 0 29 21" width="18" height="10" class="fill-none stroke-current" />
                                </router-link>
                                <Pagination :limit="3" :data="$store.getters['posts/getByKey']('index')" @pagination-change-page="paginate" />
                            </div>
                        </div>
                    </div>
                    <div class="lg:w-1/4 lg:pl-8">
                        <SidebarList />
                    </div>
                </div>
            </div>
        </div>
    </transition>
</template>

<script>
import _ from 'lodash';
import { TickerTape } from 'vue-tradingview-widgets';
import { mapGetters } from 'vuex';
import api from '../api';
import { useCcm19Global } from '../useCcm19Global';

export default {
    components: {
        TickerTape
    },
    setup() {
        const { onReady, tradingView } = useCcm19Global();
        onReady(() => {});

        return { tradingView };
    },

    mounted() {
        this.loading = true;
        const promisses = [];

        promisses.push(
            this.indexContentstreams().then(() => {
                this.loading = false;
            })
        );

        promisses.push(
            api.get('/api/v1/posts/filters').then((response) => {
                this.filters = response.data;
            })
        );

        Promise.all(promisses).then(() => {
            this.loading = false;
        });
    },

    methods: {
        paginate(page) {
            this.indexContentstreams({
                page
            });
        },

        reset() {
            this.filterOpened = false;
            this.date = null;
            this.selectedUsers = [];
            this.selectedTags = [];
            this.indexContentstreams();
        },

        setDate(date) {
            this.date = [new Date(this.$moment(date[0]).startOf('day')), new Date(this.$moment(date[1]).endOf('day'))];
        },

        search() {
            this.indexContentstreams();
        },

        selectUser(user_id) {
            if (this.selectedUsers.includes(user_id)) {
                this.selectedUsers.splice(this.selectedUsers.indexOf(user_id), 1);
            } else {
                this.selectedUsers.push(user_id);
            }
        },

        async indexContentstreams(params) {
            this.loading = true;
            const config = {
                params: {
                    user_id: this.selectedUsers,
                    post_tags: this.selectedTags,
                    post_categories: ['Finanzbildung'],
                    published_at: this.date,
                    key: 'index',
                    'sort[by]': 'created_at',
                    'sort[order]': this.sortBy
                }
            };

            _.merge(config.params, params);

            return await this.$store.dispatch('posts/index', config).then(() => {
                this.loading = false;
                this.activeTopPost = _.first(this.$store.getters['posts/getByKey']('index').data).id;
            });
        }
    },

    computed: {
        link() {
            return this.getApplicationType() === 'backend' ? '/dashboard/hkcmnews/' : '/hkcmnews/';
        },
        ...mapGetters('settings', {
            getApplicationType: 'getApplicationType'
        }),

        region() {
            return window.meta.region_configuration.region || 'de';
        },
        calendarLocale() {
            return this.region === 'de' ? 'de-DE' : 'en-GB';
        },
        calendarModeltype() {
            return this.region === 'de' ? 'dd.mm.yyyy' : 'yyyy-mm-dd';
        }
    },

    data: () => ({
        tickerTapeOptions: {
            symbols: [
                {
                    proName: 'FOREXCOM:SPXUSD',
                    title: 'S&P 500'
                },
                {
                    proName: 'BITSTAMP:BTCUSD',
                    title: 'Bitcoin'
                },
                {
                    proName: 'BITSTAMP:ETHUSD',
                    title: 'Ethereum'
                },
                {
                    description: 'Nasdaq 100',
                    proName: 'CME_MINI:NQ1!'
                },
                {
                    description: 'DJIA',
                    proName: 'CBOT_MINI:YM1!'
                },
                {
                    description: 'DAX',
                    proName: 'XETR:DAX'
                },
                {
                    description: 'Gold',
                    proName: 'COMEX_MINI:QO1!'
                },
                {
                    description: 'Silber',
                    proName: 'COMEX:SI1!'
                },
                {
                    description: 'WTI',
                    proName: 'NYMEX:CL1!'
                },
                {
                    description: 'Erdgas',
                    proName: 'CURRENCYCOM:NATURALGAS'
                },
                {
                    description: 'EUR/USD',
                    proName: 'FX:EURUSD'
                }
            ],
            showSymbolLogo: true,
            colorTheme: 'light',
            isTransparent: false,
            displayMode: 'adaptive',
            locale: 'de'
        },
        loading: true,
        activeTopPost: 0,
        filterOpened: false,
        filters: [],
        selectedUsers: [],
        selectedTags: [],
        selectedCategories: [],
        date: null,
        sortBy: 'desc',
        sortByOptions: [
            {
                value: 'asc',
                label: 'älteste zuerst'
            },
            {
                value: 'desc',
                label: 'neuste zuerst'
            }
        ]
    })
};
</script>
